import { IconBrandYoutube, IconEdit, IconTrash, IconExternalLink, IconLoader2, IconFileDescription, IconAlertCircle, IconListDetails } from "@tabler/icons-react";
import { useState, useEffect, useRef, useCallback } from "react";
import { TranscriptPanel } from "./TranscriptPanel";

// Extract YouTube video ID from various URL formats
const extractVideoId = (url: string): string | null => {
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
    /youtube\.com\/shorts\/([^&\n?#]+)/,
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match?.[1]) return match[1];
  }
  return null;
};

export interface YouTubeAnalysis {
  summary: string;
  key_points: string[];
  topics: string[];
  learning_objectives: string[];
}

export interface TranscriptSegment {
  text: string;
  start: number;
  duration: number;
}

export interface YouTubeBlockContent {
  videoUrl?: string | null;
  analyzing?: boolean;
  video_id?: string;
  transcript_available?: boolean;
  transcript_length?: number;
  transcript?: TranscriptSegment[];  // Timestamped segments
  analysis?: YouTubeAnalysis;
  error?: string;
}

interface YouTubeBlockProps {
  videoUrl?: string;
  content?: YouTubeBlockContent;
  onSubmit?: (url: string) => void;
  onRemove?: () => void;
  onOpenInTab?: () => void;
}

export const YouTubeBlock = ({ videoUrl, content, onSubmit, onRemove, onOpenInTab }: YouTubeBlockProps) => {
  const [url, setUrl] = useState('');
  const [isEditing, setIsEditing] = useState(!videoUrl);
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [showTranscript, setShowTranscript] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);

  const playerRef = useRef<YT.Player | null>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const timeIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const videoId = videoUrl ? extractVideoId(videoUrl) : null;
  const isAnalyzing = content?.analyzing === true;
  const hasAnalysis = content?.analysis != null;
  const hasTranscript = content?.transcript && content.transcript.length > 0;
  // Show error only if explicitly set AND no analysis exists
  const analysisError = content?.error && !hasAnalysis;

  const handleSubmit = () => {
    if (url.trim()) {
      const extractedId = extractVideoId(url.trim());
      if (extractedId) {
        onSubmit?.(url.trim());
        setUrl('');
        setIsEditing(false);
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    }
  };

  // Start time polling for transcript sync
  const startTimePolling = useCallback(() => {
    if (timeIntervalRef.current) return;
    timeIntervalRef.current = setInterval(() => {
      if (playerRef.current?.getCurrentTime) {
        try {
          setCurrentTime(playerRef.current.getCurrentTime());
        } catch { /* Player not ready */ }
      }
    }, 250); // Faster polling for smoother highlight
  }, []);

  // Initialize YouTube IFrame API when transcript is shown
  useEffect(() => {
    if (!showTranscript || !videoId) return;

    // Load YouTube IFrame API if not already loaded
    if (!window.YT) {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      const firstScriptTag = document.getElementsByTagName('script')[0];
      firstScriptTag.parentNode?.insertBefore(tag, firstScriptTag);
    }

    const initPlayer = () => {
      if (window.YT && window.YT.Player && iframeRef.current) {
        playerRef.current = new window.YT.Player(iframeRef.current, {
          events: { onStateChange: () => startTimePolling() }
        });
      }
    };

    if (window.YT && window.YT.Player) {
      initPlayer();
    } else {
      (window as typeof window & { onYouTubeIframeAPIReady?: () => void }).onYouTubeIframeAPIReady = initPlayer;
    }

    startTimePolling();

    return () => {
      if (timeIntervalRef.current) {
        clearInterval(timeIntervalRef.current);
        timeIntervalRef.current = null;
      }
    };
  }, [showTranscript, videoId, startTimePolling]);

  // Handle seeking when clicking transcript segments
  const handleSeek = useCallback((time: number) => {
    if (playerRef.current?.seekTo) {
      playerRef.current.seekTo(time, true);
      setCurrentTime(time);
    }
  }, []);

  // Show video player if we have a valid video
  if (videoId && !isEditing) {
    return (
      <div className="pt-4 space-y-3">
        {/* Video Player */}
        <div className="relative aspect-video rounded-xl overflow-hidden bg-black group">
          <iframe
            ref={iframeRef}
            id={`youtube-player-${videoId}`}
            src={`https://www.youtube.com/embed/${videoId}?enablejsapi=1&origin=${typeof window !== 'undefined' ? window.location.origin : ''}`}
            title="YouTube video player"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            className="absolute inset-0 w-full h-full"
          />

          {/* Overlay actions on hover */}
          <div className="absolute top-2 right-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              onClick={() => setIsEditing(true)}
              className="p-2 bg-black/70 hover:bg-black/90 rounded-lg text-white transition-colors"
              title="Change video"
            >
              <IconEdit className="w-4 h-4" />
            </button>
            {onOpenInTab && (
              <button
                onClick={onOpenInTab}
                className="p-2 bg-black/70 hover:bg-blue-600 rounded-lg text-white transition-colors"
                title="Open in new tab"
              >
                <IconExternalLink className="w-4 h-4" />
              </button>
            )}
            {onRemove && (
              <button
                onClick={onRemove}
                className="p-2 bg-black/70 hover:bg-red-600 rounded-lg text-white transition-colors"
                title="Remove video"
              >
                <IconTrash className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* Analysis Status Bar */}
        <div className="flex items-center justify-between text-xs">
          <div className="flex items-center gap-2 text-slate-400">
            <IconBrandYoutube className="w-3.5 h-3.5 text-red-400" />
            <span className="truncate max-w-[200px]">{videoUrl}</span>
          </div>

          {/* Analysis status */}
          <div className="flex items-center gap-3">
            {isAnalyzing && (
              <span className="flex items-center gap-1.5 text-blue-500">
                <IconLoader2 className="w-3.5 h-3.5 animate-spin" />
                <span>Analyzing transcript...</span>
              </span>
            )}
            {!isAnalyzing && hasAnalysis && (
              <button
                onClick={() => {
                  setShowAnalysis(!showAnalysis);
                  if (!showAnalysis) setShowTranscript(false);
                }}
                className={`flex items-center gap-1.5 transition-colors ${showAnalysis
                  ? 'text-emerald-700 font-medium'
                  : 'text-emerald-600 hover:text-emerald-700'
                  }`}
              >
                <IconFileDescription className="w-3.5 h-3.5" />
                <span>{showAnalysis ? 'Hide' : 'Show'} summary</span>
              </button>
            )}
            {!isAnalyzing && hasTranscript && (
              <button
                onClick={() => {
                  setShowTranscript(!showTranscript);
                  if (!showTranscript) setShowAnalysis(false);
                }}
                className={`flex items-center gap-1.5 transition-colors ${showTranscript
                  ? 'text-blue-700 font-medium'
                  : 'text-blue-600 hover:text-blue-700'
                  }`}
              >
                <IconListDetails className="w-3.5 h-3.5" />
                <span>{showTranscript ? 'Hide' : 'Show'} transcript</span>
              </button>
            )}
            {!isAnalyzing && analysisError && (
              <span className="flex items-center gap-1.5 text-amber-500">
                <IconAlertCircle className="w-3.5 h-3.5" />
                <span>No transcript available</span>
              </span>
            )}
          </div>
        </div>

        {/* Analysis Panel */}
        {showAnalysis && content?.analysis && (
          <div className="space-y-4 p-4 bg-slate-50 rounded-xl border border-slate-100">
            {/* Summary */}
            <div>
              <h4 className="text-sm font-medium text-slate-700 mb-2">Summary</h4>
              <p className="text-sm text-slate-600 leading-relaxed">{content.analysis.summary}</p>
            </div>

            {/* Key Points */}
            {content.analysis.key_points.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-slate-700 mb-2">Key Points</h4>
                <ul className="space-y-1">
                  {content.analysis.key_points.map((point, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                      <span className="text-emerald-500 mt-0.5">•</span>
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Topics */}
            {content.analysis.topics.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-slate-700 mb-2">Topics</h4>
                <div className="flex flex-wrap gap-1.5">
                  {content.analysis.topics.map((topic, i) => (
                    <span key={i} className="px-2 py-0.5 bg-slate-200 text-slate-600 rounded-full text-xs">
                      {topic}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Learning Objectives */}
            {content.analysis.learning_objectives.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-slate-700 mb-2">What you'll learn</h4>
                <ul className="space-y-1">
                  {content.analysis.learning_objectives.map((obj, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                      <span className="text-blue-500 mt-0.5">✓</span>
                      <span>{obj}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Transcript info */}
            {content.transcript_available && (
              <div className="pt-2 border-t border-slate-200 text-xs text-slate-400">
                Transcript: {content.transcript_length?.toLocaleString()} characters
              </div>
            )}
          </div>
        )}

        {/* Transcript Panel */}
        {showTranscript && content?.transcript && content.transcript.length > 0 && (
          <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
            <TranscriptPanel
              transcript={content.transcript}
              currentTime={currentTime}
              onSeek={handleSeek}
              maxHeight="300px"
              showHeader={true}
            />
          </div>
        )}
      </div>
    );
  }

  // Show URL input form
  return (
    <div className="pt-4">
      <div className="space-y-3">
        {/* URL Input */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 bg-white border border-slate-200 rounded-xl p-2 focus-within:border-slate-300 transition-all">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <div className="p-2 rounded-lg bg-red-50 flex-shrink-0">
              <IconBrandYoutube className="w-5 h-5 text-red-500" />
            </div>
            <input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="https://youtube.com/watch?v=..."
              className="flex-1 min-w-0 bg-transparent border-none outline-none text-sm text-slate-700 placeholder:text-slate-400"
              autoFocus
            />
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 flex-shrink-0">
            <button
              onClick={handleSubmit}
              disabled={!url.trim() || !extractVideoId(url.trim())}
              className={`px-4 py-3 sm:py-2 rounded-lg font-medium text-sm transition-all whitespace-nowrap w-full sm:w-auto ${url.trim() && extractVideoId(url.trim())
                ? 'bg-red-500 text-white hover:bg-red-600'
                : 'bg-slate-100 text-slate-400'
                }`}
            >
              {videoUrl ? 'Update' : 'Add Video'}
            </button>
            {videoUrl && (
              <button
                onClick={() => setIsEditing(false)}
                className="px-3 py-3 sm:py-2 rounded-lg text-sm text-slate-500 hover:bg-slate-100 transition-colors whitespace-nowrap w-full sm:w-auto"
              >
                Cancel
              </button>
            )}
          </div>
        </div>

        {/* Validation hint */}
        {url.trim() && !extractVideoId(url.trim()) && (
          <p className="text-xs text-red-400">
            Please enter a valid YouTube URL
          </p>
        )}

        {/* Hints */}
        <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs text-slate-400">
          <span>Supported:</span>
          <code className="px-1.5 py-0.5 bg-slate-100 rounded">youtube.com/watch?v=...</code>
          <code className="px-1.5 py-0.5 bg-slate-100 rounded">youtu.be/...</code>
        </div>
      </div>
    </div>
  );
};
